<template>
  <div>
    <div class="d-flex mb-3 align-items-end">
      <h1 class="h4 primary--text">
        {{ $t('views.email_list.email_list') }}
      </h1>
      <div class="ml-auto">
        <v-btn
          outlined
          large
          @click="toggleFilter"
        >
          <v-icon left>
            mdi-filter-variant
          </v-icon>
          {{ $t('common.filter') }}
        </v-btn>
      </div>
    </div>
    <emails-filter
      :is-shown="isFilterShown"
      :filter="filter"
      @applyFilter="applyFilter"
    />
    <v-data-table
      fixed-header
      :headers="headers"
      :items="items"
      :footer-props="{ itemsPerPageOptions: [10, 20, 50, 100] }"
      :options.sync="options"
      :server-items-length="total"
      :loading="loading"
      :loading-text="$t('common.loading_data')"
    >
      <template v-slot:item.timestamp="{ item }">
        {{ item.timestamp && $moment(item.timestamp).format('L LT') }}
      </template>
      <template v-slot:item.to="{ item }">
        {{ item.to.join(', ') }}
      </template>
      <template v-slot:item.cc="{ item }">
        {{ item.cc.join(', ') }}
      </template>
      <template v-slot:item.dealers="{ item }">
        {{ item.dealers.join(', ') }}
      </template>
      <template v-slot:item.actions="{ item }">
        <v-hover v-slot="{ hover }">
          <v-icon
            class="mr-4"
            :class="hover ? '' : 'text--disabled'"
            color="primary"
            size="20"
            @click="$router.push(`/emails/list/${item.id}`)"
          >
            mdi-email-outline
          </v-icon>
        </v-hover>
        <v-hover v-slot="{hover}">
          <v-icon
            :class="hover ? '' : 'text--disabled'"
            color="primary"
            size="20"
            @click="downloadMail(item.id)"
          >
            mdi-download
          </v-icon>
        </v-hover>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import EmailsFilter from '@/components/emails/EmailsFilter';

export default {
  name: 'EmailList',

  components: {
    EmailsFilter,
  },

  data() {
    return {
      items: [],
      total: 0,
      options: {
        sortBy: ['timestamp'],
        sortDesc: [true],
      },

      headers: [
        {
          text: this.$t('views.email_list.event'),
          value: 'event',
        },
        {
          text: this.$t('common.date'),
          value: 'timestamp',
        },
        {
          text: this.$t('views.email_list.subject'),
          value: 'subject',
        },
        {
          text: this.$t('views.email_list.to'),
          value: 'to',
        },
        {
          text: this.$t('views.email_list.cc'),
          value: 'cc',
        },
        {
          text: this.$t('common.dealers'),
          value: 'dealers',
        },
        {
          text: this.$t('common.actions'),
          value: 'actions',
          sortable: false,
          width: 150,
          align: 'center',
        },
      ],

      loading: false,
      isFilterShown: false,

      filter: {
        dealer: '',
        type: '',
        to: '',
      },

      EventTypes: {
        system: 'system',
        business: 'business',
      },
    };
  },

  computed: {
    ...mapState({
      user: 'user',
      dealerData: 'dealer',
    }),
    ...mapGetters(['business']),
  },

  watch: {
    options: {
      handler() {
        this.getItems();
      },
      deep: true,
    },
  },

  created() {
    this.init();
  },

  methods: {
    init() {
      if (!this.business) {
        this.filter.dealer = this.user.asdb;
        this.filter.type = this.EventTypes.business;
      }
      if (this.dealerData && this.dealerData.timezone) {
        this.$moment.tz(this.dealerData.timezone);
      } else {
        this.$moment.tz.setDefault();
      }
    },

    async getItems() {
      const {
        sortBy, sortDesc, page, itemsPerPage,
      } = this.options;

      const filter = this.mapFilter();

      const params = {};
      const { eventType, ...query } = filter;
      params.query = query;
      if (eventType) {
        params.eventType = eventType;
      }
      params.pageSize = itemsPerPage;
      params.page = page;
      if (sortBy.length) {
        params.sort = `${sortDesc[0] ? '-' : ''}${sortBy[0]}`;
      }

      this.loading = true;

      try {
        const { data } = await this.$http.get('mails', { params });
        this.items = data.rows.map((email) => ({
          ...email,
          timestamp: email.timestamp && new Date(email.timestamp),
        }));
        this.total = data.total;
      } finally {
        this.loading = false;
      }
    },

    toggleFilter() {
      this.isFilterShown = !this.isFilterShown;
    },

    applyFilter(filter) {
      this.filter = { ...filter };
      this.getItems();
    },

    mapFilter() {
      const filter = {};
      if (this.filter.dealer) {
        if (this.filter.type === this.EventTypes.system) {
          filter.dealers = this.filter.dealer;
        } else {
          filter.dealers = {
            $in: [[], this.filter.dealer],
          };
        }
      }
      if (this.filter.type) {
        filter.eventType = this.filter.type;
      } else if (this.filter.dealer) {
        filter.eventType = this.EventTypes.business;
      }
      if (this.filter.to) {
        filter.to = this.filter.to;
      }
      return filter;
    },

    async downloadMail(id) {
      const { data } = await this.$http.get(`/mails/${id}/eml`, {
        headers: {
          'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.style.display = 'none';
      link.setAttribute('href', url);
      link.setAttribute('download', 'mail.eml');
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
  },
};
</script>
